import { startTelemetry } from "./telemetry.ts";
import "maplibre-gl/dist/maplibre-gl.css";
import "uplot/dist/uPlot.min.css";
import "./styles.css";
import { askBox, metricFirst, routeQuery } from "./ask.ts";
import { api, setBudgetQuery, type GeoInfo, type Level, type Metric } from "./api.ts";
import { budgetQuery, loadBudget, PERSONAL_IDS, saveBudget, type Budget } from "./budget.ts";
import { budgetBox } from "./budgetbox.ts";
import { COVERAGE } from "../shared/coverage.ts";
import { DATA_CREDIT, fmt, LEVEL_LABEL, LEVEL_PLURAL, periodLabel } from "../shared/format.ts";
import { el, nodes } from "./dom.ts";
import type { View } from "./map.ts";
import { MapView } from "./mapview.ts";
import { DetailPanel } from "./panel.ts";
import { TableView } from "./table.ts";
import { activeRank, describeScreen, screenParams, type Screen } from "./screen.ts";
import { ScreenerView, type ScreenLevel } from "./screener.ts";
import { CompareView } from "./compare.ts";
import { MAX_COMPARE, readHash, stateToHash } from "./hash.ts";
import { levelInScope, scopeCategories, settle, shownAvailability, stateFromLink, switchScope, unavailableReason, withArea, withLevel, withMetric,
  withQuestion, type Catalog, type Facts, type State } from "./state.ts";
import { pageOf } from "./routes.ts";
import { Store } from "./store.ts";
import { BAY_AREA_ID, DEEP_STATE, depthLine } from "../shared/regions.ts";
import { RelateView } from "./relate.ts";
import { BRAND } from "../shared/brand.ts";
import { areaTitle, marketPath } from "../shared/marketpath.ts";
import { account, accountChip, lockedTeaser, openSignup, renderAccount, welcomeBack } from "./account.ts";

/** The level bar, California's levels first: city and ZIP, out to the national map. */
const LEVEL_BAR: Level[] = ["city", "zip", "county", "metro", "state", "us"];
/** Areas the table can be filtered to, narrowest first, for each table level (metros carry no state, so none for them). */
const CONTAINERS: Record<Level, Level[]> = { zip: ["county", "metro", "state"], city: ["county", "metro", "state"], county: ["metro", "state"], metro: [], state: [], us: [] };
/** The deep state's market page (the footer's Markets, the fallback when the map can't load). */
const DEEP_MARKET = marketPath({ level: "state", code: DEEP_STATE.fips, name: DEEP_STATE.name });


async function main() {
  startTelemetry(); // every page: the report, listing analyzer, About / Help and the map
  welcomeBack(); // just signed in by an emailed link: a welcome note
  // pages of their own (web/routes.ts): no map; any later hash change reloads into whatever the link opens. Each loads
  // its module when opened (D5): most visits never open one
  const page = pageOf(location.hash);
  if (page) {
    const root = document.getElementById("app")!;
    const reload = () => location.reload();
    if (page.page === "report") {
      // printable report (opened in a new tab from the area panel); a free account's when gated
      await account.load(await api.config().catch(() => null));
      if (account.gated) {
        const g = await api.geo(page.geo!).catch(() => null);
        frameGate(root, g ? areaTitle(g) : null, page.geo!);
      } else await (await import("./report.ts")).renderReport(root, page.geo!);
    } else if (page.page === "listing") {
      // the listing analyzer keeps its inputs in the link and rewrites it itself: only leaving it reloads
      await (await import("./listing.ts")).renderListing(root, page.query);
      window.addEventListener("hashchange", () => { if (pageOf(location.hash)?.page !== "listing") reload(); });
      return;
    } else if (page.page === "panel") await (await import("./embed.ts")).renderEmbeddedPanel(root, page.geo!, page.query); // the iOS app's web view (web/embed.ts)
    else if (page.page === "account") await renderAccount(root);
    else if (page.page === "about") await (await import("./about.ts")).renderAbout(root);
    else if (page.page === "terms") await (await import("./legal.ts")).renderTerms(root);
    else if (page.page === "privacy") await (await import("./legal.ts")).renderPrivacy(root);
    else (await import("./about.ts")).renderHelp(root);
    window.addEventListener("hashchange", reload);
    return;
  }
  performance.mark("app:start");
  const [cfg, reg] = await Promise.all([api.config(), api.metrics(true)]);
  await account.load(cfg); // who's signed in (the gate; a quick private request)
  // downloads (the table's and screener's Export CSV links) come with a free account when gated
  document.addEventListener("click", (e) => {
    const a = (e.target as HTMLElement).closest?.('a[href*="format=csv"]');
    if (a && account.gated) { e.preventDefault(); openSignup({ source: "csv", dismissible: true }); }
  }, true);
  performance.mark("app:registry");
  const metrics = new Map(reg.metrics.map((m) => [m.id, m]));
  // personal metrics (from the user's budget) exist only once a budget is set; "Where can I afford" leads with them
  const personalAvail = new Map(reg.metrics.filter((m) => m.personal).map((m) => [m.id, m.available]));
  // what /api/metrics says exists, before the budget and the scope narrow it (every view reads m.available)
  const servedAvail = new Map(reg.metrics.map((m) => [m.id, m.available]));
  // the live values that aren't in the URL (web/store.ts): the opened area as loaded, and the user's budget
  const store = new Store<{ area: GeoInfo | null; budget: Budget | null }>({ area: null, budget: loadBudget() });
  /**
   * Each data point's levels with data as the views should see them: personal ones only with a budget; in the U.S.
   * view no regional data point (metrics.yaml coverage) and no City level. The table, Screener, Compare, search and
   * Ask's views all read this, so they follow the budget and the scope without knowing about either.
   */
  const applyAvailability = () => {
    const budget = store.get("budget");
    setBudgetQuery(budgetQuery(budget));
    for (const m of metrics.values()) m.available = shownAvailability(m, servedAvail.get(m.id) ?? {}, state.scope, !!budget);
  };
  /** the data points as the state rules (web/state.ts) see them: what /api/metrics serves, and whether a budget is set */
  const catalog = (): Catalog => ({
    metric: (id) => {
      const m = metrics.get(id);
      return m && { levels: m.levels, coverage: m.coverage, cadence: m.cadence, personal: m.personal, available: servedAvail.get(id) ?? {} };
    },
    hasBudget: !!store.get("budget"),
  });
  const afford = reg.questions.find((q) => q.id === "afford");
  if (afford) afford.metrics = ["my_payment_pct", ...afford.metrics.filter((id) => !PERSONAL_IDS.includes(id))];
  const questionById = (id: string | null | undefined) => reg.questions.find((q) => q.id === id);
  const isMetric = (id: string) => metrics.has(id);
  // a link to a level the app doesn't serve (metrics.yaml hidden_levels:, e.g. an old city link) falls back to the default
  const served = (id: string) => reg.levels.includes(id.split(":")[0] as Level);
  const servedHash = (): Partial<State> => {
    const h = readHash(location.hash, isMetric);
    if (h.level && !reg.levels.includes(h.level)) delete h.level;
    if (h.geo && !served(h.geo)) delete h.geo;
    if (h.compare) h.compare = h.compare.filter(served);
    return h;
  };
  // what the scope rules (web/state.ts) need to know about data points, questions and cities
  const cityCounty = cfg.cityCounty ?? {};
  const facts: Facts = { coverage: (id) => metrics.get(id)?.coverage, questionMetrics: (id) => questionById(id)?.metrics, cityCounty: (id) => cityCounty[id] ?? null };
  /**
   * The live state. Only `go` (below) changes it: every path (a click, a link, Back / Forward, Ask, the panel's links)
   * builds the next state with the rules in web/state.ts and hands it to `go`, which applies what changed.
   */
  const state: State = settle(stateFromLink(servedHash(), facts), facts, catalog());
  applyAvailability();
  const metric = () => metrics.get(state.metric)!;

  /** Mirror the state in the URL. push = a navigation (area or view) that Back should undo; everything else replaces. */
  const writeHash = (push = false) => {
    const url = stateToHash(state);
    if (url === location.hash) return;
    if (push) history.pushState(null, "", url);
    else history.replaceState(null, "", url);
  };

  // ---------- layout ----------
  const app = document.getElementById("app")!;
  const sidebar = el("aside", { class: "sidebar", "aria-label": "Data points" });
  const mapEl = el("div", { class: "map", id: "map" });
  const panelEl = el("section", { class: "detail", hidden: true, "aria-label": "Area details" });
  const tableEl = el("section", { class: "table-view", hidden: true, "aria-label": "Ranking table" });
  const accEl = el("section", { class: "table-view accuracy", hidden: true, "aria-label": "Forecast accuracy" });
  const screenEl = el("section", { class: "table-view screener", hidden: true, "aria-label": "Screener" });
  const compareEl = el("section", { class: "table-view compare", hidden: true, "aria-label": "Compare areas" });
  const relateEl = el("section", { class: "table-view compare relate", hidden: true, "aria-label": "How two data points relate" });
  const screenChip = el("div", { class: "screen-chip", hidden: true, role: "status" });
  const tooltip = el("div", { class: "tooltip", hidden: true });
  const legend = el("div", { class: "legend" });
  const zoomHint = el("button", { class: "zoom-hint", hidden: true }, "");
  const compareBtn = el("button", { class: "btn small", onclick: () => setView(state.view === "compare" ? "map" : "compare") }, "⇆ Compare");
  // the map's own settings (month, labels, color scope) live in the legend; the bar opens the other views
  const bottomBar = el("div", { class: "bottom-bar" },
    el("button", { class: "btn small", onclick: () => setView(state.view === "table" ? "map" : "table") }, "▦ Table View"),
    el("button", { class: "btn small", onclick: () => setView(state.view === "screen" ? "map" : "screen") }, "⚲ Screener"),
    compareBtn,
    el("a", { class: "btn small", href: "#/listing" }, "Before you offer"));
  // small screens: the sidebar is a drawer over the map; the scrim, its ✕ and Escape close it
  const closeSidebar = () => app.classList.remove("sidebar-open");
  const toggleSidebar = el("button", { class: "btn icon sidebar-toggle", "aria-label": "Toggle data points", onclick: () => app.classList.toggle("sidebar-open") },
    el("span", { class: "burger", "aria-hidden": "true" }));
  const scrim = el("div", { class: "sidebar-scrim", onclick: closeSidebar });
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeSidebar(); });
  // California first (any U.S. place still works); the U.S. view asks for any place
  const searchHint = () => (state.scope === "ca" ? (cfg.ask ? "Search a California city, or ask" : "Search a California city or ZIP")
    : cfg.ask ? "Search a place, or ask" : "Search a city, county or ZIP");
  const SEARCH_HINT = searchHint();
  // small screens: the search box lives in the drawer, so the top of the map has a bar that opens it with the box focused
  const focusSearch = () => { app.classList.add("sidebar-open"); search.focus(); };
  const phoneSearch = el("button", { class: "phone-search", type: "button", onclick: () => focusSearch() },
    el("span", { "aria-hidden": "true" }, "⌕"), SEARCH_HINT);
  // first visit: what this is and two ways in; dismissed for good once closed or once a place is opened
  const WELCOME_KEY = "monitusre.welcome.v1";
  const seen = (() => { try { return !!localStorage.getItem(WELCOME_KEY); } catch { return true; } })();
  const welcome = el("div", { class: "welcome", hidden: seen || /geo=|[?&]f=/.test(location.hash), role: "note" });
  const dismissWelcome = () => {
    welcome.hidden = true;
    try { localStorage.setItem(WELCOME_KEY, "1"); } catch { /* private mode: shows again next time */ }
  };
  welcome.append(
    el("button", { class: "btn icon welcome-close", "aria-label": "Close", onclick: dismissWelcome }, "✕"),
    // phones: the brand is otherwise only in the menu, and the long text covered half the map
    el("div", { class: "welcome-brand logo" }, el("img", { class: "logo-mark", src: "/favicon.svg", alt: "" }), BRAND.parts[0], el("b", {}, BRAND.parts[1])),
    el("p", { class: "welcome-long" }, el("b", {}, `${BRAND.tagline}.`),
      ` Prices against local incomes, who has the upper hand in a deal, and a 1-year outlook that shows `,
      el("a", { href: "/scorecard" }, "its own track record"), `, for every U.S. state, metro, county and ZIP. ${depthLine()}.`),
    el("p", { class: "welcome-short" }, el("b", {}, `${BRAND.tagline}.`), " Prices, leverage and a forecast with ",
      el("a", { href: "/scorecard" }, "its track record"), `. ${DEEP_STATE.name} goes deepest.`),
    el("div", { class: "welcome-actions" },
      el("button", { class: "btn small primary", onclick: () => { dismissWelcome(); focusSearch(); } }, "Look up your city"),
      // California's own questions (metrics.yaml coverage): opening one switches the map to California if need be
      ...reg.questions.filter((q) => (q.coverage ?? "us") !== "us").slice(0, 2).map((q) =>
        el("button", { class: "btn small", onclick: () => { dismissWelcome(); startQuestion(q.id); } }, q.title)),
      el("button", { class: "btn small welcome-us", onclick: () => { dismissWelcome(); setScope("us"); } }, "See the whole U.S.")));
  app.replaceChildren(sidebar, scrim, el("main", { class: "main" }, mapEl, toggleSidebar, phoneSearch, screenChip, welcome, zoomHint, legend, bottomBar, tooltip, panelEl, tableEl, accEl, screenEl, compareEl, relateEl));

  // ---------- sidebar ----------
  // one box: places and data points as you type, Enter opens the top one or asks the question (routeQuery)
  const search = el("input", { type: "search", placeholder: SEARCH_HINT, "aria-label": SEARCH_HINT, autocomplete: "off", maxlength: 400 });
  const results = el("ul", { class: "search-results", role: "listbox", hidden: true });
  // the two views (web/state.ts): California with every layer, or the U.S. with the national data points
  const scopeBar = el("div", { class: "levels scopes", role: "radiogroup", "aria-label": "California or the whole U.S." },
    ...(["ca", "us"] as const).map((sc) => el("button", { role: "radio", class: "level", "data-scope": sc, onclick: () => setScope(sc) },
      sc === "ca" ? "California" : "Whole U.S.")));
  const levelBar = el("div", { class: "levels", role: "radiogroup", "aria-label": "Geography level" },
    ...LEVEL_BAR.filter((l) => reg.levels.includes(l)).map((l) => el("button", { role: "radio", class: "level", "data-level": l, onclick: () => pickLevel(l) }, LEVEL_LABEL[l]!)));
  const metricList = el("div", { class: "metric-list" });
  const build = cfg.build?.latest_period ? `Data through ${periodLabel(cfg.build.latest_period.slice(0, 7))}` : "No data built yet";
  const ask = askBox({
    metrics,
    context: () => ({ level: state.level, metric: state.metric, geo: state.geo, scope: state.scope }),
    showPlaces: (lvl, items) => void mapView.pinAnswer(lvl, items, { open: openPlace, highlight: (id) => ask.highlight(id) }),
    pickPlace: (id) => openPlace(id),
    focusPlace: (id) => map.focusHit(id?.split(":")[1] ?? null),
    onClear: () => { search.value = ""; },
    useBudget: (b) => store.set("budget", b),
  });
  const budgetCard = budgetBox({
    get: () => store.get("budget"),
    latestRate: reg.latest_rate,
    set: (b, show) => {
      store.set("budget", b); // applied everywhere by the subscriber below
      closeSidebar();
      if (show) setMetric("my_payment_pct");
    },
  });
  /**
   * The one place a budget change is kept and applied, whether it came from the card or from an Ask answer: the
   * personal data points' values and levels change, so the map, panel, table, Screener and sidebar follow it.
   */
  store.on("budget", (b) => {
    saveBudget(b);
    applyAvailability();
    budgetCard.render();
    const personalNow = personalAvail.has(state.metric);
    if (!b && personalNow) setMetric("payment_pct_income"); // its data is gone: the closest data point that needs no budget
    else if (personalNow) { void mapView.load(true); if (panel.open) void panel.refreshChart(); }
    renderMetrics();
    table.refresh();
    screener.refresh();
    void loadScreen();
  });
  sidebar.append(
    el("div", { class: "brand" }, el("span", { class: "logo" }, el("img", { class: "logo-mark", src: "/favicon.svg", alt: "" }), BRAND.parts[0], el("b", {}, BRAND.parts[1])),
      el("span", { class: "brand-tag" }, BRAND.tagline), el("span", { class: "muted small" }, build),
      account.gate || account.signedIn ? accountChip(() => { const g = store.get("area"); return g ? { geo: g.geo_id, name: areaTitle(g) } : null; }) : null,
      el("button", { class: "btn icon sidebar-close", "aria-label": "Close data points", onclick: closeSidebar }, "✕")),
    el("div", { class: "search" }, search, results),
    ask.el,
    el("div", { class: "levels-wrap" }, el("span", { class: "levels-label" }, "Show"), scopeBar),
    el("div", { class: "levels-wrap" }, el("span", { class: "levels-label" }, "Map areas by"), levelBar),
    metricList,
    el("footer", { class: "muted small" },
      el("div", { class: "footer-links" }, el("a", { href: DEEP_MARKET }, "Markets"), el("a", { href: "#/about" }, "About"), el("a", { href: "#/help" }, "Help"),
        el("a", { href: "#/terms" }, "Terms"), el("a", { href: "#/privacy" }, "Privacy")),
      `${DATA_CREDIT}. Not financial advice.`));

  const unavailable = (m: Metric) => unavailableReason(m, state.scope, state.level, !!store.get("budget"));
  /**
   * My budget lives in "Where can I afford to buy?" (not at the top of the sidebar: most visitors come to look, not to
   * type their income). The places that need it open that question with the form.
   */
  const openBudget = () => {
    if (state.q !== "afford") startQuestion("afford");
    app.classList.add("sidebar-open");
    budgetCard.edit();
  };
  const cats = [...reg.categories];
  const byCat = (c: string) => reg.metrics.filter((m) => m.category === c);
  const open = new Set<string>();
  const renderMetrics = () => {
    const shown = scopeCategories(cats, reg.metrics, state.scope);
    metricList.replaceChildren(renderQuestions(), el("h4", { class: "side-head" }, "All data points"), ...shown.map((c) => {
      const items = byCat(c);
      const isOpen = open.has(c);
      // where the data exists (metrics.yaml coverage): on the category when all of it is regional, else on each row
      const covs = new Set(items.map((m) => m.coverage));
      const catRegion = covs.size === 1 && !covs.has("us") ? items[0]!.coverage : null;
      const region = (m: Metric) => (m.coverage === "us" || catRegion ? null
        : el("span", { class: "tag region", title: `${COVERAGE[m.coverage].only}: other areas have no data` }, COVERAGE[m.coverage].name));
      return el("div", { class: `cat ${isOpen ? "open" : ""}` },
        el("button", { class: "cat-head", "aria-expanded": String(isOpen), onclick: () => { isOpen ? open.delete(c) : open.add(c); renderMetrics(); } },
          el("span", { class: "cat-name" }, c), catRegion ? el("span", { class: "tag region" }, COVERAGE[catRegion].only) : null,
          el("span", { class: "cat-count" }, String(items.length)), el("span", { class: "caret", "aria-hidden": "true" }, "›")),
        isOpen ? el("ul", {}, ...items.map((m) => {
          const avail = m.levels.includes(state.level) && !!m.available[state.level];
          return el("li", {},
            el("label", { class: `metric ${avail ? "" : "disabled"} ${m.id === state.metric ? "active" : ""}`, title: avail ? m.description : unavailable(m) },
              el("input", { type: "radio", name: "metric", value: m.id, checked: m.id === state.metric, disabled: !avail, onchange: () => setMetric(m.id) }),
              el("span", { class: "metric-label" }, m.label), region(m),
              el("span", { class: "hint", title: `${m.description}\nSource: ${m.source}` }, "i")));
        })) : null);
    }));
  };

  // Questions first: each picks a few metrics that answer it; the active one shows them as chips.
  const available = (m: Metric | undefined) => !!m && m.levels.includes(state.level) && !!m.available[state.level];
  /** Open a guided question (its sidebar button, the welcome card): in California when it only works there. */
  const startQuestion = (id: string) => {
    const q = questionById(id);
    if (q) go(withQuestion(state, q, facts, catalog()));
  };
  // California's questions (ids "ca-…") and its forecast scorecard first, then the ones that work anywhere
  // the California view: its own questions (metrics.yaml coverage: ca) and its forecast scorecard first, then the ones
  // that work anywhere; the U.S. view: those, and the national accuracy page
  const renderQuestions = () => {
    const ca = state.scope === "ca";
    const record = el("a", { class: "question question-link", href: ca ? "/scorecard" : "#/accuracy" },
      el("span", { class: "question-title" }, "Has the forecast been right?"),
      el("span", { class: "question-blurb muted small" }, ca ? "What it said vs. what happened, for every county and city" : "Its record against the naive guess, by level"));
    const national = reg.questions.filter((q) => (q.coverage ?? "us") === "us").map(questionItem);
    return el("section", { class: "questions", "aria-label": "Start with a question" }, ...nodes(
      ca ? el("h4", { class: "side-head" }, "California") : null,
      ...(ca ? reg.questions.filter((q) => (q.coverage ?? "us") !== "us").map(questionItem) : []),
      ca ? record : null,
      el("h4", { class: "side-head" }, ca ? "Anywhere in the U.S." : "Questions"),
      ...national,
      ca ? null : record));
  };
  function questionItem(q: (typeof reg.questions)[number]) {
      const active = q.id === state.q;
      return el("div", { class: `question ${active ? "active" : ""}` },
        el("button", { class: "question-head", "aria-expanded": String(active),
          onclick: () => (active ? go({ ...state, q: null }) : startQuestion(q.id)) },
          el("span", { class: "question-title" }, q.title), el("span", { class: "question-blurb muted small" }, q.blurb)),
        active ? el("div", { class: "question-body" },
          el("div", { class: "question-metrics", role: "radiogroup", "aria-label": q.title },
            ...q.metrics.map((id) => {
              const m = metrics.get(id);
              if (!m) return null;
              const ok = available(m);
              return el("button", { role: "radio", class: `qchip ${id === state.metric ? "active" : ""}`, "aria-checked": String(id === state.metric),
                disabled: !ok, title: ok ? m.description : unavailable(m),
                onclick: () => setMetric(id, true) }, m.label);
            })),
          el("p", { class: "question-read muted small" }, q.read),
          q.id === "afford" ? budgetCard.el : null) : null);
  }

  let searchAbort: AbortController | undefined;
  search.addEventListener("input", async () => {
    if (!welcome.hidden) dismissWelcome(); // they've started: it only covers the map now
    searchAbort?.abort();
    const q = search.value.trim();
    if (q.length < 2) { results.hidden = true; return; }
    searchAbort = new AbortController();
    try {
      const { results: rs } = await api.search(q, searchAbort.signal);
      const ms = matchMetrics(q);
      const metricRows = ms.map((m) => {
        const ok = available(m);
        const choose = () => { if (ok) pickMetric(m.id); };
        return el("li", { role: "option", tabindex: 0, class: ok ? "" : "disabled", "aria-disabled": String(!ok), title: ok ? m.description : unavailable(m),
          onclick: choose, onkeydown: (e: Event) => { if ((e as KeyboardEvent).key === "Enter") choose(); } },
          el("span", {}, m.label), el("span", { class: "tag" }, "Data point"));
      });
      const placeRows = rs.map((r) => el("li", { role: "option", tabindex: 0,
        onclick: () => pick(r.geo_id, r.level), onkeydown: (e: Event) => { if ((e as KeyboardEvent).key === "Enter") pick(r.geo_id, r.level); } },
        el("span", {}, r.level === "zip" ? `${r.code} · ${r.name}` : r.name), el("span", { class: "tag" }, LEVEL_LABEL[r.level]!)));
      const metricsUp = metricFirst(q, rs[0] ? { name: rs[0].name, code: rs[0].code } : null, ms[0]?.label ?? null);
      const askRow = cfg.ask && q.length >= 3
        ? el("li", { role: "option", tabindex: 0, class: "ask-row", onclick: () => askNow(q), onkeydown: (e: Event) => { if ((e as KeyboardEvent).key === "Enter") askNow(q); } },
            el("span", {}, "Ask: ", el("em", {}, `“${q}”`)), el("span", { class: "tag" }, "Question"))
        : null;
      results.replaceChildren(...nodes(...(metricsUp ? [...metricRows, ...placeRows] : [...placeRows, ...metricRows]), askRow));
      results.hidden = !results.children.length;
    } catch { /* aborted */ }
  });
  // Enter: the search may still be in flight, so look the top place up again (fast) before deciding
  search.addEventListener("keydown", async (e) => {
    if (e.key !== "Enter") return;
    const q = search.value.trim();
    if (!q) return;
    searchAbort?.abort();
    const { results: rs } = await api.search(q).catch(() => ({ results: [] as Awaited<ReturnType<typeof api.search>>["results"] }));
    if (search.value.trim() !== q) return;
    const top = rs[0];
    const m = matchMetrics(q)[0];
    const route = routeQuery(q, top ? { name: top.name, code: top.code } : null, cfg.ask, m?.label ?? null);
    if (route === "metric") { if (m && available(m)) pickMetric(m.id); }
    else if (route === "place") { if (top) pick(top.geo_id, top.level); }
    else askNow(q);
  });
  search.addEventListener("keydown", (e) => { if (e.key === "Escape") results.hidden = true; });
  /** Data points for the search box: names starting with the text first, then names containing it, then descriptions (popular ones first in each); five at most. */
  const matchMetrics = (q: string) => {
    const t = q.trim().toLowerCase();
    if (t.length < 2) return [];
    const rank = (m: Metric) => { const l = m.label.toLowerCase(); return l.startsWith(t) ? 0 : l.includes(t) ? 1 : `${m.category} ${m.description}`.toLowerCase().includes(t) ? 2 : 3; };
    return reg.metrics.map((m) => ({ m, r: rank(m) })).filter((x) => x.r < 3).sort((a, b) => a.r - b.r || Number(!!b.m.popular) - Number(!!a.m.popular)).slice(0, 5).map((x) => x.m);
  };
  const pickMetric = (id: string) => {
    results.hidden = true;
    closeSidebar();
    search.value = "";
    open.add(metrics.get(id)!.category);
    setMetric(id);
  };
  const askNow = (q: string) => { results.hidden = true; ask.run(q); };
  const pick = (geoId: string, level: Level) => {
    results.hidden = true;
    closeSidebar();
    search.value = "";
    openArea(geoId, { level: level !== "us" });
  };

  // ---------- map, panel, table ----------
  // a link to the national or state map opens on the lower 48, everything else on the Bay Area
  const openView: View = state.scope === "us" ? "us" : state.level === "state" ? "ca" : "bay";
  // the map and what sits on it (web/mapview.ts): legend, month, labels, zoom hint, tooltip, pins
  const mapView = new MapView({ map: mapEl, legend, tooltip, zoomHint }, {
    cfg,
    get: () => ({ level: state.level, metric: metric(), period: state.period }),
    onClick: (id) => openArea(id, { level: false, fly: false }),
    onHoverHit: (hit) => { ask.highlight(hit); if (screener.docked) screener.highlight(hit); },
    onPeriod: (period) => go({ ...state, period }),
    onShown: () => { if (table.open) table.refresh(); },
  }, openView);
  const map = mapView.map;

  const panel = new DetailPanel(panelEl, {
    gated: true,
    metrics: reg.metrics,
    getMetric: metric,
    setMetric: (id) => setMetric(id),
    // California's data points are hidden in the U.S. view: open them in California's
    showCaMetric: (id) => go(withMetric(state.scope === "ca" ? state : switchScope(state, "ca", facts), id, facts, catalog()), { push: true }),
    getFreq: () => state.freq,
    setFreq: (f) => go({ ...state, freq: f }),
    onClose: () => go({ ...state, geo: null }, { push: true }),
    onCompare: (id) => addToCompare(id),
    onNavigate: (id) => openArea(id),
    budget: { has: () => !!store.get("budget"), open: () => openBudget(),
      show: () => go(withMetric({ ...state, q: "afford" }, "my_payment_pct", facts, catalog(), true)) },
  });
  const table = new TableView(tableEl, {
    metrics,
    getLevel: () => state.level,
    getMetric: metric,
    getWithin: () => within(),
    clearWithin: () => {
      withinOff = true;
      if (state.within) go({ ...state, within: null }, { push: true }); // refreshes the table
      else table.refresh();
    },
    onRowClick: (id) => openArea(id, { level: false, map: true }),
    onClose: () => setView("map"),
  });
  const compareView = new CompareView(compareEl, {
    metrics,
    getIds: () => state.compare,
    setIds: (ids) => go({ ...state, compare: ids }),
    getMetric: metric,
    setMetric: (id) => setMetric(id),
    getFreq: () => state.freq,
    onOpenArea: (id) => openPlace(id),
    onClose: () => setView("map"),
  });
  const relateView = new RelateView(relateEl, {
    metrics,
    get: () => ({ level: state.level, x: state.metric, y: state.y ?? null, within: state.within ?? null }),
    set: (p) => {
      let next: State = { ...state, ...(p.y !== undefined ? { y: p.y } : {}), ...(p.within !== undefined ? { within: p.within } : {}) };
      if (p.level) next = withLevel(next, p.level);
      if (p.x) next = withMetric(next, p.x, facts, catalog());
      go(next);
    },
    onOpenArea: (id) => openPlace(id),
    onClose: () => setView("map"),
  });
  const updateCompareBtn = () => { compareBtn.textContent = state.compare.length ? `⇆ Compare (${state.compare.length})` : "⇆ Compare"; };
  updateCompareBtn();
  const addToCompare = (id: string) => {
    const compare = state.compare.includes(id) ? state.compare : [...state.compare, id].slice(-MAX_COMPARE);
    go({ ...state, compare, view: "compare" }, { push: true });
  };

  const screener = new ScreenerView(screenEl, {
    metrics,
    categories: reg.categories,
    getLevel: () => screenLevel(),
    setLevel: (l) => setLevel(l),
    getScreen: () => state.screen,
    setScreen: (sc) => go({ ...state, screen: sc }, { from: "screener" }), // the Screener has drawn its own form
    getMapMetric: metric,
    onRowClick: (id) => openPlace(id),
    onRowHover: (id) => map.focusHit(id?.split(":")[1] ?? null),
    onPins: (lvl, rows, first) => void mapView.pinScreen(lvl, rows, first, { open: openPlace, highlight: (id) => screener.highlight(id) }),
    onShowMap: () => showScreenOnMap(),
    onEdit: () => { undockScreener(); setView("screen"); },
    onNeedBudget: () => openBudget(),
    onClose: () => { undockScreener(); screener.hide(); setView("map"); },
  });
  /**
   * Show on map: the results dock beside the map with numbered pins, the map colors by the screen's first data point
   * (the ranking's first, else the first filter's) so a match's color means something, and every area that doesn't
   * pass fades.
   */
  const showScreenOnMap = () => {
    const sc = state.screen;
    const first = [...(sc ? activeRank(sc) : []), ...sc?.filters ?? []].find((f) => metrics.get(f.metric)?.available[screenLevel()])?.metric;
    let next: State = { ...state, view: "map" };
    if (first) next = withMetric(next, first, facts, catalog());
    if (next.level === "us") next = withLevel(next, "state"); // the national map has no areas to mark
    go(next, { push: true });
    screener.dock();
    app.classList.add("screen-docked");
  };
  const undockScreener = () => app.classList.remove("screen-docked");

  // a screen's matches stay marked on the map (everything else fades) until it's cleared
  let screenSeq = 0;
  const loadScreen = async () => {
    const mine = ++screenSeq;
    const sc = state.screen;
    const lvl = screenLevel();
    if (!sc || state.level === "us") { map.setScreen(lvl, null); screenChip.hidden = true; return; }
    try {
      const r = await api.screen(lvl, screenParams(sc, true), { limit: 1, codes: true });
      if (mine !== screenSeq) return;
      map.setScreen(lvl, r.codes ?? []);
      screenChip.hidden = false;
      screenChip.replaceChildren(
        el("span", {}, el("strong", {}, `${r.total.toLocaleString()} ${r.total === 1 ? LEVEL_LABEL[lvl] : LEVEL_PLURAL[lvl]} match`),
          el("span", { class: "muted small" }, ` · ${describeScreen(sc, metrics, (m, v) => fmt(m, v, true))}`)),
        el("button", { class: "btn small", onclick: () => setView("screen") }, "Edit"),
        el("button", { class: "btn icon", "aria-label": "Clear screen", onclick: () => {
          if (screener.docked) { screener.hide(); undockScreener(); }
          go({ ...state, screen: null }); // an open Screener redraws without it
        } }, "✕"));
    } catch {
      if (mine === screenSeq) { map.setScreen(lvl, null); screenChip.hidden = true; }
    }
  };

  // The table is filtered to the narrowest area around the selection (the selection itself or one of its parents)
  // that can contain the table's level, e.g. ZIP table + selected ZIP -> its county. "Show all" turns that off
  // until the next selection. Otherwise the deep state's view ranks the deep state's areas (metros by their main
  // state), and "show all" there means all of it: the whole country is the U.S. view's.
  let withinOff = false;
  const within = (): { geo_id: string; name: string; scope?: true } | null => {
    const lvl = state.level === "us" ? "state" : state.level;
    const scope = state.scope === "ca" && lvl !== "state" ? { geo_id: DEEP_STATE.geoId, name: DEEP_STATE.name, scope: true as const } : null;
    // cleared with "(show all)", which drops state.within; held only at the levels the Bay Area holds
    if (state.within === BAY_AREA_ID) return ["county", "zip", "city"].includes(state.level) ? { geo_id: BAY_AREA_ID, name: "the Bay Area" } : scope;
    const selected = store.get("area");
    if (!selected || withinOff) return scope;
    const around = [{ geo_id: selected.geo_id, name: selected.name }, ...selected.parents];
    for (const c of CONTAINERS[lvl]) {
      const hit = around.find((a) => a.geo_id.startsWith(`${c}:`));
      if (hit) return hit;
    }
    return scope;
  };

  // ---------- the one state transition ----------
  /**
   * How a transition is applied. push: a navigation (an area, a view, the scope) that Back should undo; everything
   * else replaces the URL. fit: move the map to a new scope's bounds (not when an area is about to be flown to).
   * fly: fly to a newly opened area. reopen: open the area again even if it's the same one (picked again).
   * from: the view the change came from, which has already redrawn itself.
   */
  interface GoOpts { push?: boolean; fit?: boolean; fly?: boolean; reopen?: boolean; from?: "screener" }
  const screenKey = (sc: Screen | null) => (sc ? screenParams(sc).toString() : "");
  /** The screener has no national level: the U.S. map screens states. */
  const screenLevelOf = (s: State): ScreenLevel => (s.level === "us" ? "state" : s.level);
  const screenLevel = () => screenLevelOf(state);
  let pendingSort: "asc" | null = null; // a link's one-shot "lowest first", for the table when it opens

  /**
   * Move to the next state: made valid (web/state.ts settle), then whatever differs from the current one is applied.
   * Every change of the state goes through here.
   */
  const go = (want: State, o: GoOpts = {}) => {
    const prev = { ...state };
    const next = settle(want, facts, catalog());
    if (next.sort) pendingSort = next.sort;
    Object.assign(state, next);
    delete state.sort;
    apply(prev, o);
  };

  /** Apply the state (prev: what was applied before; null at startup, when everything is new). */
  const apply = (prev: State | null, o: GoOpts) => {
    const s = state;
    const changed = <K extends keyof State>(k: K, key: (v: State[K]) => unknown = (v) => v ?? null) => !prev || key(prev[k]) !== key(s[k]);
    const scope = changed("scope"), level = changed("level"), metricCh = changed("metric"), geo = changed("geo");
    const compare = changed("compare", (ids) => ids.join());
    const relate = changed("y") || changed("within");
    const screen = changed("screen", screenKey) || !prev || screenLevelOf(prev) !== screenLevelOf(s);
    if (scope) {
      applyAvailability();
      if (prev && o.fit !== false) map.showView(s.scope === "us" ? "us" : "ca");
    }
    syncChrome();
    if (level || metricCh || changed("period")) void mapView.load();
    if (geo || (o.reopen && s.geo)) {
      if (s.geo) void loadArea(s.geo, o.fly ?? true);
      else if (prev) clearArea();
    } else if ((metricCh || changed("freq")) && panel.open) void panel.refreshChart();
    if (prev) {
      // the views that read the state redraw (each does nothing while closed); the map's load refreshes the table
      if (compare || metricCh || scope) compareView.refresh();
      if (relate || metricCh || level) relateView.refresh();
      if (scope || changed("within")) table.refresh();
      if ((screen || scope) && o.from !== "screener") screener.refresh();
    }
    if (screen) void loadScreen();
    syncView(prev?.view ?? null);
    writeHash(o.push ?? false);
  };

  /** The sidebar and the bars around the map, from the state: every transition runs it (Back across a scope too). */
  const syncChrome = () => {
    const hint = searchHint();
    if (search.placeholder !== hint) {
      search.placeholder = hint;
      search.setAttribute("aria-label", hint);
      phoneSearch.lastChild!.textContent = hint;
    }
    for (const b of scopeBar.querySelectorAll<HTMLElement>(".level")) {
      b.classList.toggle("active", b.dataset.scope === state.scope);
      b.setAttribute("aria-checked", String(b.dataset.scope === state.scope));
    }
    for (const b of levelBar.querySelectorAll<HTMLElement>(".level")) {
      b.hidden = !levelInScope(state.scope, b.dataset.level as Level);
      b.classList.toggle("active", b.dataset.level === state.level);
      b.setAttribute("aria-checked", String(b.dataset.level === state.level));
    }
    updateCompareBtn();
    renderMetrics();
  };

  /** Show the state's view (prev: the view shown before; null at startup). */
  const syncView = (prev: State["view"] | null) => {
    const v = state.view;
    const changed = v !== prev;
    tableEl.hidden = v !== "table";
    accEl.hidden = v !== "accuracy";
    if (v !== "screen" && !(v === "map" && screener.docked)) { screener.hide(); undockScreener(); }
    if (v !== "compare") compareView.hide();
    if (v !== "relate") relateView.hide();
    // the area panel sits above the map; over a table or the accuracy page it would hide them (it returns on the map)
    app.classList.toggle("overlay-open", v !== "map");
    if (v === "table") {
      const fresh = changed || !table.open;
      if (pendingSort) { table.setSort(state.metric, pendingSort); pendingSort = null; if (!fresh) table.refresh(); }
      if (fresh) table.show();
    }
    if (v === "accuracy" && changed) void import("./accuracy.ts").then((a) => a.renderAccuracy(accEl, () => setView("map")));
    if (v === "screen" && (changed || !screener.open)) screener.show();
    if (v === "compare" && (changed || !compareView.open)) compareView.show();
    if (v === "relate" && (changed || !relateView.open)) relateView.show();
  };

  const setView = (v: State["view"], push = true) => go({ ...state, view: v }, { push });

  // A pasted or edited link, or Back / Forward: the state it spells out (web/state.ts stateFromLink).
  const applyHash = () => {
    // pages of their own (report, embedded panel, listing analyzer, about, help) start from a fresh load
    if (pageOf(location.hash)) return location.reload();
    // a bare view link such as "#/accuracy" changes only the view; a full link spells out the whole state
    const full = /[?=]/.test(location.hash);
    go(stateFromLink(servedHash(), facts, full ? null : state));
  };
  window.addEventListener("hashchange", applyHash);
  window.addEventListener("popstate", applyHash);

  /**
   * Open an area (web/state.ts withArea): at its own level unless `level: false`, in its scope, back on the map with
   * `map`. Picking the open area again opens it again (flies back to it).
   */
  const openArea = (id: string, o: { level?: boolean; map?: boolean; fly?: boolean } = {}) =>
    go(withArea(state, id, facts, { level: o.level ?? true, map: o.map ?? false }), { push: true, fit: false, fly: o.fly ?? true, reopen: true });
  /** a place from a list (Compare, the relationship view, the Screener, Ask's answer, pins): on the map */
  const openPlace = (id: string) => openArea(id, { map: true });
  const setLevel = (l: Level) => go(withLevel(state, l));
  /** the level bar: National and State zoom out to the lower 48 from the California view (one area otherwise) */
  const pickLevel = (l: Level) => {
    if ((l === "us" || l === "state") && map.zoom() > 4) map.showView("us");
    setLevel(l);
  };
  /** keepQuestion: the pick came from the active question's chips; a pick from the full list leaves the question. */
  const setMetric = (id: string, keepQuestion = false) => go(withMetric(state, id, facts, catalog(), keepQuestion));

  /**
   * Switch between the California and U.S. views (web/state.ts switchScope: City becomes County, an open city its
   * county, a regional data point Home Value, …). The map moves to the view's bounds.
   */
  const setScope = (to: State["scope"]) => go(switchScope(state, to, facts), { push: true, fly: false });

  /** Close the open area on screen: the panel, the map's outline and the loaded area (go has cleared state.geo). */
  const clearArea = () => {
    panel.close();
    store.set("area", null);
    map.select(null);
  };

  /** Load the open area into the panel, outline it on the map and (fly) fly to it. */
  const loadArea = async (geoId: string, fly: boolean) => {
    if (!welcome.hidden) dismissWelcome();
    const [lvl, code] = geoId.split(":") as [Level, string | undefined];
    map.select(lvl, code);
    store.set("area", null);
    withinOff = false;
    const geo = api.geo(geoId); // one request for the panel and the table's "within"
    const [g] = await Promise.all([geo.catch(() => null), panel.show(geoId, geo)]);
    if (state.geo !== geoId) return; // another area was picked meanwhile
    store.set("area", g);
    if (table.open) table.refresh();
    if (g?.lat != null && g.lon != null && fly) map.flyTo(g.lon, g.lat, lvl);
  };

  // Nothing waits for the map: it loads its style and tiles from the basemap service (slow on a first visit, and not
  // at all in a background tab), while the panel, table and screener only need the API.
  pendingSort = state.sort ?? null;
  delete state.sort;
  apply(null, { fit: false });
}

main().catch((e) => {
  // visitors get a way on (the market pages are plain HTML: no WebGL needed); the developer's hint shows only locally
  const msg = String((e as Error)?.message ?? e);
  const noGl = /webgl/i.test(msg) || !document.createElement("canvas").getContext("webgl2");
  const local = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname);
  document.getElementById("app")!.replaceChildren(el("div", { class: "fatal" },
    el("h1", {}, BRAND.name),
    el("p", {}, noGl ? "Your browser can't draw the map. " : "The map couldn't load just now. ",
      el("a", { href: DEEP_MARKET }, `Browse ${DEEP_STATE.name} markets instead →`)),
    el("p", {}, el("a", { href: "/scorecard" }, "See how the forecast has done"), " · ", el("a", { href: "/market/us" }, "The U.S. market")),
    local ? el("p", { class: "muted small" }, `Dev: ${msg}. On a fresh install, run \`bun run ingest\` and \`bun run tiles\`, then reload.`) : null));
});

/** The report page without an account (gated): what the report holds and the sign-up. */
function frameGate(root: HTMLElement, name: string | null, geoId: string) {
  document.title = `${BRAND.name} – Market report`;
  root.replaceChildren(el("div", { class: "listing doc" }, el("div", { class: "report-card doc-body" },
    el("h1", {}, `${name ?? "Market"} report`),
    lockedTeaser("The printable market report", "Valuation, buyer leverage, the 12-month outlook with its drivers, and the history, on a few pages you can save as PDF.",
      { source: "report", geo: geoId, geoName: name, next: `/${location.hash}` }),
    el("p", { class: "muted small" }, el("a", { href: "#/" }, "← Back to the map")))));
  openSignup({ source: "report", geo: geoId, geoName: name, next: `/${location.hash}`, dismissible: true });
}
